/**
 * Share text for WhatsApp and anywhere else people paste things.
 *
 * The team already lives in WhatsApp. Rather than competing for their
 * attention with an in-app feed nobody opens, the app produces a clean summary
 * that one tap drops into whatever group they already use.
 *
 * This deliberately uses a `wa.me` link rather than the WhatsApp Business API:
 * the API cannot post into a group that already exists, its Groups feature caps
 * at eight participants and requires a verified business account, and it costs
 * money per message. A share link has none of those constraints and works on
 * every phone today.
 *
 * All formatting is pure so it can be tested without a browser.
 */

/** "14:30" -> "2:30 PM". Duplicated from mealTypes deliberately — this module
 *  stays dependency-free so it can be reused or moved without dragging icons in. */
function time12(value) {
  if (!value) return "";
  const [h, m] = String(value).split(":").map(Number);
  if (Number.isNaN(h)) return "";
  const suffix = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m || 0).padStart(2, "0")} ${suffix}`;
}

function money(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

/** Today's plan — what a coordinator posts on the morning of an event. */
export function todaySummary(data) {
  if (!data?.is_event_day) {
    return `${data?.event_title || "Event"} — nothing scheduled for ${data?.date || "today"}.`;
  }
  const lines = [
    `📅 *${data.event_title}*`,
    `Day ${data.day_number}${data.day_name ? ` · ${data.day_name}` : ""}`,
  ];
  if (data.location) lines.push(`📍 ${data.location}`);
  lines.push("");

  for (const meal of data.meals || []) {
    const when = [meal.start_time, meal.end_time].filter(Boolean).map(time12).join("–");
    const parts = [`🍽 *${meal.meal_type}*`];
    if (when) parts.push(when);
    if (meal.vendor_name) parts.push(meal.vendor_name);
    lines.push(parts.join(" · "));
  }
  if (!(data.meals || []).length) lines.push("_No meals planned for today._");

  const done = data.counts?.served || 0;
  const total = (data.meals || []).length;
  if (total) {
    lines.push("");
    lines.push(`✅ ${done} of ${total} served`);
  }
  return lines.join("\n");
}

/** The full plan — what gets shared when an event is confirmed. */
export function eventSummary({ event, meals = [], getVendorName = () => "" }) {
  if (!event) return "";
  const lines = [`📅 *${event.title}*`];
  if (event.location) lines.push(`📍 ${event.location}`);
  lines.push(
    `🗓 ${event.start_date}${event.end_date && event.end_date !== event.start_date
      ? ` – ${event.end_date}` : ""}`
  );

  for (const day of event.days || []) {
    const dayMeals = meals.filter((m) => m.day_number === day.day_number);
    if (!dayMeals.length) continue;
    lines.push("");
    lines.push(`*Day ${day.day_number}${day.day_name ? ` — ${day.day_name}` : ""}*`);
    for (const meal of dayMeals) {
      const when = [meal.start_time, meal.end_time].filter(Boolean).map(time12).join("–");
      const vendor = meal.vendor_id ? getVendorName(meal.vendor_id) : "";
      lines.push(
        `• ${meal.meal_type}${when ? ` (${when})` : ""}${vendor ? ` — ${vendor}` : ""}`
      );
    }
  }
  return lines.join("\n");
}

/** After the event — the numbers worth telling people. */
export function wrapUpSummary({ actuals, comparison }) {
  if (!actuals) return "";
  const lines = [`📊 *${actuals.event_title} — wrap-up*`, ""];
  if (actuals.total_planned) {
    lines.push(`Planned: ${actuals.total_planned}`);
    lines.push(`Served: ${actuals.total_served}`);
    lines.push(`Left over: ${actuals.total_leftover}`);
  }
  if (actuals.waste_percent != null) lines.push(`Waste: ${actuals.waste_percent}%`);

  const current = (comparison?.events || []).find((e) => e.is_current);
  if (current?.cost_per_head != null) {
    lines.push("");
    lines.push(`Cost per head: ${money(current.cost_per_head)}`);
  }
  const change = comparison?.change;
  if (change?.cost_per_head_change != null) {
    const sign = change.cost_per_head_change > 0 ? "+" : "";
    lines.push(
      `vs ${change.compared_to}: ${sign}${money(change.cost_per_head_change)} per head`
    );
  }
  return lines.join("\n");
}

/** Registration invite, for pasting into a group. */
export function registrationInvite({ eventTitle, url, message = "" }) {
  const lines = [`📅 *${eventTitle}*`, "", "Registration is open:", url];
  if (message) {
    lines.splice(1, 0, "");
    lines.splice(2, 0, message);
  }
  return lines.join("\n");
}

/** A wa.me link that opens WhatsApp with the text ready to send. */
export function whatsappLink(text) {
  return `https://wa.me/?text=${encodeURIComponent(text || "")}`;
}

/**
 * Share via the OS sheet where available, otherwise open WhatsApp directly.
 *
 * The native sheet is better when present — it offers WhatsApp alongside
 * everything else — but it only exists in secure contexts on some browsers, so
 * the WhatsApp link is the fallback rather than the other way round.
 */
export async function shareText(text, { title = "" } = {}) {
  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      await navigator.share({ title, text });
      return "shared";
    } catch (err) {
      // A user cancelling the sheet is not a failure to fall back from.
      if (err?.name === "AbortError") return "cancelled";
    }
  }
  if (typeof window !== "undefined") {
    window.open(whatsappLink(text), "_blank", "noopener");
    return "whatsapp";
  }
  return "unavailable";
}

/** Clipboard fallback, for desktop browsers with neither of the above. */
export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
